import { Button, Tooltip } from 'antd';
import { MinusOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getRandomColor = () =>
  '#' + Math.floor(Math.random() * 16777215).toString(16);

export type PageMeta = {
  pageNumber: number;
  width: number;
  height: number;
  unit: string;
};
export type FieldHit = {
  value: string;
  pageNumber: number;
  polygons: number[][];
};

type Props = {
  base64: string;
  pages: PageMeta[];
  hits: Record<string, FieldHit | null>;
  scale?: number;
};

export default function PdfWithBoxesReducto({
  base64,
  pages,
  hits,
  scale: initialScale = 1.5,
}: Props) {
  const [scale, setScale] = useState(initialScale);
  const [pageSizes, setPageSizes] = useState<
    Map<number, { width: number; height: number }>
  >(new Map());

  const dataUrl = useMemo(
    () =>
      base64.startsWith('data:')
        ? base64
        : `data:application/pdf;base64,${base64}`,
    [base64],
  );

  // Group hits by page
  const overlays = useMemo(() => {
    const map = new Map<number, { label: string; polygons: number[][] }[]>();
    Object.entries(hits).forEach(([label, hit]) => {
      if (!hit) return;
      if (!map.has(hit.pageNumber)) map.set(hit.pageNumber, []);
      map.get(hit.pageNumber)!.push({ label, polygons: hit.polygons });
    });
    return map;
  }, [hits]);

  console.log('overlays', overlays);
  console.log('pages', pages);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#f5f5f5',
        padding: '1rem',
      }}
    >
      {/* Zoom Controls */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: 8 }}>
        <Tooltip title="Zoom Out">
          <Button
            icon={<MinusOutlined />}
            onClick={() => setScale((s) => Math.max(0.5, s - 0.2))}
          />
        </Tooltip>
        <Tooltip title="Reset Zoom">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setScale(initialScale)}
          />
        </Tooltip>
        <Tooltip title="Zoom In">
          <Button
            icon={<PlusOutlined />}
            onClick={() => setScale((s) => Math.min(3, s + 0.2))}
          />
        </Tooltip>
        <span style={{ marginLeft: 8 }}>Zoom: {(scale * 100).toFixed(0)}%</span>
      </div>

      <Document file={dataUrl} loading={<div>Loading PDF…</div>}>
        {pages.map((pm) => (
          <Page
            key={pm.pageNumber}
            pageNumber={pm.pageNumber}
            scale={scale}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onLoadSuccess={(page) => {
              const [x1, y1, x2, y2] = page.view;
              const pdfWidth = x2 - x1;
              const pdfHeight = y2 - y1;
              setPageSizes((prev) =>
                new Map(prev).set(pm.pageNumber, {
                  width: pdfWidth,
                  height: pdfHeight,
                }),
              );
              console.log(
                `📄 Page ${pm.pageNumber}: ${pdfWidth} × ${pdfHeight}`,
              );
            }}
          >
            {() => {
              const pageDim = pageSizes.get(pm.pageNumber);
              if (!pageDim) return null;

              const overlayItems = overlays.get(pm.pageNumber) ?? [];
              const { width: pdfWidth, height: pdfHeight } = pageDim;

              return (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: pdfWidth,
                    height: pdfHeight,
                    fontFamily: 'monospace',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#0f0',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                    }}
                  >
                    PDF {pm.pageNumber}: {Math.round(pdfWidth)} ×{' '}
                    {Math.round(pdfHeight)} px
                  </div>

                  {overlayItems.flatMap((item, idx) =>
                    item.polygons.map((poly, j) => {
                      const [nTop, nLeft, nWidth, nHeight] = poly;

                      // Compute original PDF pixel coordinates
                      const x1 = nLeft * pdfWidth;
                      const y1 = (1 - nTop - nHeight) * pdfHeight;
                      const boxW = nWidth * pdfWidth;
                      const boxH = nHeight * pdfHeight;

                      // Convert to DOM coordinates
                      const left = x1;
                      const top = pdfHeight - y1 - boxH;

                      // ✅ Apply scale to match Page zoom
                      const scaledLeft = left * scale;
                      const scaledTop = top * scale;
                      const scaledW = boxW * scale;
                      const scaledH = boxH * scale;

                      return (
                        <div key={`${idx}-${j}`}>
                          {/* Highlight Box */}
                          <Tooltip
                            title={`${item.label} -> ${hits[item.label]?.value}`}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                left: scaledLeft,
                                top: scaledTop,
                                width: scaledW,
                                height: scaledH,
                                border: `2px solid ${getRandomColor()}`,
                                backgroundColor: 'rgba(255, 0, 0, 0.15)',
                                borderRadius: 4,
                                cursor: 'pointer',
                              }}
                            />
                          </Tooltip>
                        </div>
                      );
                    }),
                  )}
                </div>
              );
            }}
          </Page>
        ))}
      </Document>
    </div>
  );
}
